import { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { useSearchParams, Link } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogDescription,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '../components/ui/dropdown-menu';
import { toast } from 'sonner';
import { Plus, FileText, MoreVertical, Paperclip, Clock, Search, Trash2, CheckSquare, Users, X, Check, Package, AlertTriangle, User, ArrowUpDown, MessageSquare, Download, FileSpreadsheet, Upload, Bookmark } from 'lucide-react';
import { format } from '../utils/dateUtils';
import { Pagination } from '../components/Pagination';

import config from '../config';
const API = `${config.BACKEND_URL}/api`;

const PRIORITIES = [
  { value: 'critical', label: 'Critical', color: 'bg-red-100 text-red-800 border-red-300' },
  { value: 'very-high', label: 'Very High', color: 'bg-orange-100 text-orange-800 border-orange-300' },
  { value: 'high', label: 'High', color: 'bg-yellow-100 text-yellow-800 border-yellow-300' },
  { value: 'medium', label: 'Medium', color: 'bg-blue-100 text-blue-800 border-blue-300' },
  { value: 'low', label: 'Low', color: 'bg-gray-100 text-gray-800 border-gray-300' }
];

import { RichTextEditor } from '../components/RichTextEditor';

export const UserStories = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [stories, setStories] = useState([]);
  const [projects, setProjects] = useState([]);
  const [releases, setReleases] = useState([]);
  const [filteredReleases, setFilteredReleases] = useState([]);
  const [users, setUsers] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  
  // Load saved filters from localStorage
  const getSavedFilters = () => {
    try {
      const saved = localStorage.getItem('userStoriesFilters');
      return saved ? JSON.parse(saved) : null;
    } catch {
      return null;
    }
  };
  
  const savedFilters = getSavedFilters();
  
  const [searchQuery, setSearchQuery] = useState(savedFilters?.searchQuery || '');
  const [filterProject, setFilterProject] = useState(searchParams.get('project') || savedFilters?.filterProject || 'all');
  const [filterStatuses, setFilterStatuses] = useState(savedFilters?.filterStatuses || []);
  const [filterPriority, setFilterPriority] = useState(savedFilters?.filterPriority || 'all');
  const [filterAssignee, setFilterAssignee] = useState(savedFilters?.filterAssignee || 'all');
  const [filterRelease, setFilterRelease] = useState(savedFilters?.filterRelease || 'all');
  const [sortBy, setSortBy] = useState(savedFilters?.sortBy || 'priority');
  const [sortOrder, setSortOrder] = useState(savedFilters?.sortOrder || 'desc');
  const [statusFilterOpen, setStatusFilterOpen] = useState(false);
  const [projectReleases, setProjectReleases] = useState([]);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const pageSize = 15;
  
  // Bulk selection state
  const [selectedStories, setSelectedStories] = useState([]);
  const [bulkDialogOpen, setBulkDialogOpen] = useState(false);
  const [bulkAction, setBulkAction] = useState('');
  const [bulkStatus, setBulkStatus] = useState('');
  const [bulkPriority, setBulkPriority] = useState('medium');
  const [bulkAssignees, setBulkAssignees] = useState([]);
  const [bulkReleaseId, setBulkReleaseId] = useState('backlog');
  
  // Inline edit state
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editField, setEditField] = useState(''); // 'priority', 'status', 'release'
  const [editStory, setEditStory] = useState(null);
  
  // Active release state
  const [activeRelease, setActiveRelease] = useState(null);
  const [closeReleaseDialogOpen, setCloseReleaseDialogOpen] = useState(false);
  const [nextReleaseOption, setNextReleaseOption] = useState('existing'); // 'existing', 'new', or 'skip'
  const [selectedNextReleaseId, setSelectedNextReleaseId] = useState('');
  const [newReleaseVersion, setNewReleaseVersion] = useState('');
  const [plannedReleases, setPlannedReleases] = useState([]);
  const [editValue, setEditValue] = useState('');

  // Shown when closing a release finds stories that aren't done/rejected yet.
  const [openStoriesDialogOpen, setOpenStoriesDialogOpen] = useState(false);
  const [openStoriesInfo, setOpenStoriesInfo] = useState(null); // { count, stories, message } from the 409 response
  const [openStoriesTargetStatus, setOpenStoriesTargetStatus] = useState('');
  
  const [form, setForm] = useState({
    title: '',
    description: '',
    project_id: '',
    release_id: '',
    priority: 'medium',
    assigned_users: [],
    internal: false,
    customer: ''
  });

  // Pending attachments for the create dialog (uploaded after story creation)
  const [pendingFiles, setPendingFiles] = useState([]);
  const [createDragActive, setCreateDragActive] = useState(false);

  // Save filters to localStorage whenever they change
  useEffect(() => {
    const filters = {
      searchQuery,
      filterProject,
      filterStatuses,
      filterPriority,
      filterAssignee,
      filterRelease,
      sortBy,
      sortOrder
    };
    localStorage.setItem('userStoriesFilters', JSON.stringify(filters));
  }, [searchQuery, filterProject, filterStatuses, filterPriority, filterAssignee, filterRelease, sortBy, sortOrder]);

  const clearFilters = () => {
    setSearchQuery('');
    setFilterProject('all');
    setFilterStatuses([]);
    setFilterPriority('all');
    setFilterAssignee('all');
    setFilterRelease('all');
    setSortBy('priority');
    setSortOrder('desc');
    setCurrentPage(1);
    localStorage.removeItem('userStoriesFilters');
  };

  const hasActiveFilters = searchQuery || filterProject !== 'all' || filterStatuses.length > 0 || filterPriority !== 'all' || filterAssignee !== 'all';

  // Saved filter views - named snapshots of the filter/sort state above,
  // persisted to localStorage so they survive reloads (no backend involved).
  const loadSavedViews = () => {
    try {
      const raw = localStorage.getItem('userStoriesSavedViews');
      return raw ? JSON.parse(raw) : [];
    } catch {
      return [];
    }
  };

  const [savedViews, setSavedViews] = useState(loadSavedViews);
  const [saveViewDialogOpen, setSaveViewDialogOpen] = useState(false);
  const [newViewName, setNewViewName] = useState('');

  const persistSavedViews = (views) => {
    setSavedViews(views);
    localStorage.setItem('userStoriesSavedViews', JSON.stringify(views));
  };

  const applyView = (view) => {
    setSearchQuery(view.filters.searchQuery || '');
    setFilterProject(view.filters.filterProject || 'all');
    setFilterStatuses(view.filters.filterStatuses || []);
    setFilterPriority(view.filters.filterPriority || 'all');
    setFilterAssignee(view.filters.filterAssignee || 'all');
    setFilterRelease(view.filters.filterRelease || 'all');
    setSortBy(view.filters.sortBy || 'priority');
    setSortOrder(view.filters.sortOrder || 'desc');
    setCurrentPage(1);
  };

  const deleteView = (viewId) => {
    persistSavedViews(savedViews.filter((v) => v.id !== viewId));
    toast.success('View deleted');
  };

  const saveCurrentAsView = () => {
    const name = newViewName.trim();
    if (!name) return;
    const view = {
      id: Date.now(),
      name,
      filters: { searchQuery, filterProject, filterStatuses, filterPriority, filterAssignee, filterRelease, sortBy, sortOrder },
    };
    persistSavedViews([...savedViews, view]);
    setNewViewName('');
    setSaveViewDialogOpen(false);
    toast.success(`Saved view "${name}"`);
  };

  // Define useCallback functions BEFORE useEffect that uses them
  const fetchStatuses = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/statuses`);
      setStatuses(response.data);
      // Set default bulk status if statuses loaded
      if (response.data.length > 0) {
        setBulkStatus(prev => prev || response.data[0].key);
      }
    } catch (error) {
      console.error('Failed to fetch statuses:', error);
    }
  }, []);

  const fetchStories = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        page: currentPage,
        page_size: pageSize,
        sort_by: sortBy,
        sort_order: sortOrder
      };
      if (filterProject !== 'all') params.project_id = filterProject;
      if (filterRelease !== 'all') params.release_id = filterRelease;
      
      // Handle status filtering
      if (filterStatuses.length === 0) {
        // Default: show only "not done" statuses
        params.status = 'active';
      } else if (filterStatuses.includes('all')) {
        // Show all statuses - don't add status param
      } else if (filterStatuses.includes('development')) {
        // Development: show only open and in-progress
        params.statuses = 'open,in-progress';
      } else {
        // Multiple statuses selected
        params.statuses = filterStatuses.join(',');
      }
      
      if (filterPriority !== 'all') params.priority = filterPriority;
      if (filterAssignee === 'me') params.assigned_to_me = true;
      else if (filterAssignee !== 'all') params.assigned_user_id = filterAssignee;
      if (searchQuery) params.search = searchQuery;

      const response = await axios.get(`${API}/user-stories/paginated`, { params });
      setStories(response.data.items);
      setTotalPages(response.data.total_pages);
      setTotalItems(response.data.total);
    } catch (error) {
      console.error('Failed to fetch stories:', error);
      toast.error('Failed to load user stories');
    } finally {
      setLoading(false);
    }
  }, [currentPage, pageSize, filterProject, filterRelease, filterStatuses, filterPriority, filterAssignee, sortBy, sortOrder, searchQuery]);

  const fetchProjects = async () => {
    try {
      const response = await axios.get(`${API}/projects`);
      setProjects(response.data);
    } catch (error) {
      console.error('Failed to fetch projects:', error);
    }
  };

  const fetchReleases = async () => {
    try {
      const response = await axios.get(`${API}/releases`);
      setReleases(response.data);
    } catch (error) {
      console.error('Failed to fetch releases:', error);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/users/list`);
      setUsers(response.data);
    } catch (error) {
      console.error('Failed to fetch users:', error);
    }
  };

  const fetchActiveRelease = useCallback(async (projectId) => {
    if (!projectId || projectId === 'all') {
      setActiveRelease(null);
      setPlannedReleases([]);
      return;
    }
    try {
      const response = await axios.get(`${API}/projects/${projectId}/active-release`);
      setActiveRelease(response.data);

      // Also fetch planned releases for the close dialog
      const releasesResponse = await axios.get(`${API}/releases`, { params: { project_id: projectId } });
      setPlannedReleases(releasesResponse.data.filter(r => r.status === 'planned'));
    } catch (error) {
      console.error('Failed to fetch active release:', error);
      setActiveRelease(null);
    }
  }, []);

  // All releases for a project, for the release filter dropdown. Exposed as
  // a callback (not just an effect) so it can also be re-run after actions
  // that add/change a release - closing a release, starting one, etc. -
  // instead of only when the project filter itself changes.
  const fetchProjectReleases = useCallback(async (projectId) => {
    if (!projectId || projectId === 'all') {
      setProjectReleases([]);
      return;
    }
    try {
      const response = await axios.get(`${API}/releases`, { params: { project_id: projectId } });
      setProjectReleases(response.data);
    } catch (error) {
      console.error('Failed to fetch project releases:', error);
      setProjectReleases([]);
    }
  }, []);

  // useEffects that depend on the callbacks above
  useEffect(() => {
    fetchProjects();
    fetchReleases();
    fetchUsers();
    fetchStatuses();
  }, [fetchStatuses]);

  useEffect(() => {
    fetchStories();
  }, [fetchStories]);

  useEffect(() => {
    if (form.project_id) {
      setFilteredReleases(releases.filter(r => r.project_id === form.project_id));
      setForm(prev => ({ ...prev, release_id: '' }));
    } else {
      setFilteredReleases([]);
    }
  }, [form.project_id, releases]);

  // Fetch active release and project releases when project filter changes
  useEffect(() => {
    if (filterProject && filterProject !== 'all') {
      fetchActiveRelease(parseInt(filterProject));
      // Fetch all releases for this project (for the filter dropdown)
      fetchProjectReleases(filterProject);
    } else {
      setActiveRelease(null);
      setPlannedReleases([]);
      setProjectReleases([]);
      setFilterRelease('all');
    }
  }, [filterProject, fetchActiveRelease, fetchProjectReleases]);

  const addPendingFiles = (fileList) => {
    const files = Array.from(fileList || []);
    if (files.length > 0) {
      setPendingFiles((prev) => [...prev, ...files]);
    }
  };

  const handleCreateDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setCreateDragActive(false);
    if (e.dataTransfer?.files?.length) {
      addPendingFiles(e.dataTransfer.files);
    }
  };

  const removePendingFile = (index) => {
    setPendingFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...form,
        release_id: form.release_id || null  // Send null if no release selected
      };
      const response = await axios.post(`${API}/user-stories`, payload);
      const newStoryId = response.data.id;

      // Upload any pending attachments to the newly created story
      if (newStoryId && pendingFiles.length > 0) {
        for (const file of pendingFiles) {
          const formData = new FormData();
          formData.append('file', file);
          try {
            await axios.post(`${API}/user-stories/${newStoryId}/attachments`, formData);
          } catch (err) {
            const detail = err.response?.data?.detail || err.message || 'Unknown error';
            toast.error(`Failed to upload ${file.name}: ${detail}`);
          }
        }
      }

      toast.success('User story created');
      setDialogOpen(false);
      setForm({ title: '', description: '', project_id: '', release_id: '', priority: 'medium', assigned_users: [], internal: false, customer: '' });
      setPendingFiles([]);
      fetchStories();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create user story');
    }
  };

  // Drop a screenshot (or any file) anywhere on the page to jump straight
  // into "New Story" with it already attached - handy for colleagues who
  // take a screenshot first and want a story built around it. Uses a
  // dragenter/dragleave counter (rather than trusting a single dragleave)
  // because those events fire per-element as the cursor crosses every
  // nested child while dragging over the page, not just page vs not-page.
  const [pageDragActive, setPageDragActive] = useState(false);
  const pageDragCounter = useRef(0);

  const isFileDrag = (e) => Array.from(e.dataTransfer?.types || []).includes('Files');

  const handlePageDragEnter = (e) => {
    if (!isFileDrag(e)) return;
    e.preventDefault();
    pageDragCounter.current += 1;
    setPageDragActive(true);
  };

  const handlePageDragOver = (e) => {
    // Required for onDrop to fire at all - browsers reject drops on
    // elements that don't cancel dragover.
    if (!isFileDrag(e)) return;
    e.preventDefault();
  };

  const handlePageDragLeave = (e) => {
    if (!isFileDrag(e)) return;
    e.preventDefault();
    pageDragCounter.current = Math.max(0, pageDragCounter.current - 1);
    if (pageDragCounter.current === 0) setPageDragActive(false);
  };

  const handlePageDrop = (e) => {
    if (!isFileDrag(e)) return;
    e.preventDefault();
    pageDragCounter.current = 0;
    setPageDragActive(false);
    const files = e.dataTransfer?.files;
    if (!files?.length) return;
    // Only reset the form when the dialog wasn't already open, so this
    // never clobbers a story a colleague is already in the middle of
    // filling in - it just attaches the file to it instead.
    if (!dialogOpen) {
      setForm({ title: '', description: '', project_id: '', release_id: '', priority: 'medium', assigned_users: [], internal: false, customer: '' });
    }
    addPendingFiles(files);
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this user story?')) return;
    try {
      await axios.delete(`${API}/user-stories/${id}`);
      toast.success('User story deleted');
      fetchStories();
    } catch (error) {
      toast.error('Failed to delete user story');
    }
  };

  const handleCloseRelease = async (openStoriesStatus) => {
    if (!activeRelease) return;

    try {
      const payload = {};
      if (nextReleaseOption === 'existing' && selectedNextReleaseId) {
        payload.next_release_id = parseInt(selectedNextReleaseId);
      } else if (nextReleaseOption === 'new' && newReleaseVersion) {
        payload.new_release_version = newReleaseVersion;
      } else if (nextReleaseOption === 'skip') {
        // Leave both unset - the backend just closes the release without
        // starting another one.
      } else {
        toast.error('Please select or create a release to continue');
        return;
      }
      if (openStoriesStatus) {
        payload.open_stories_status = openStoriesStatus;
      }

      const response = await axios.post(`${API}/releases/${activeRelease.id}/close`, payload);
      toast.success(response.data.message);
      setCloseReleaseDialogOpen(false);
      setOpenStoriesDialogOpen(false);
      setOpenStoriesInfo(null);
      setOpenStoriesTargetStatus('');
      setNextReleaseOption('existing');
      setSelectedNextReleaseId('');
      setNewReleaseVersion('');
      fetchActiveRelease(parseInt(filterProject));
      fetchProjectReleases(filterProject);
      fetchReleases();
      fetchStories();
    } catch (error) {
      const detail = error.response?.data?.detail;
      // The backend still refuses to close a release with stories that
      // aren't done/rejected yet unless told what to do with them - this
      // is normally already caught by the pre-check below before the user
      // even gets here, so this only fires if a story became open again in
      // the meantime (e.g. another user re-opened one mid-flow).
      if (detail && typeof detail === 'object' && detail.error === 'open_stories_exist') {
        setOpenStoriesInfo(detail);
        setOpenStoriesTargetStatus('');
        setOpenStoriesDialogOpen(true);
        return;
      }
      toast.error((typeof detail === 'string' && detail) || 'Failed to close release');
    }
  };

  // Statuses that can be used to resolve stories still open when closing a
  // release - done statuses first, then rejected ones (matching the Close
  // Story dropdown on the story detail page).
  const closeableStatuses = [
    ...statuses.filter((s) => s.is_done),
    ...statuses.filter((s) => s.is_rejected && !s.is_done),
  ];

  const [checkingOpenStories, setCheckingOpenStories] = useState(false);

  // Entry point for "Close current release": check for still-open stories
  // *before* asking the user to pick/create a next release, so cancelling
  // out of that warning never leaves a next release half set up.
  const handleOpenCloseReleaseClick = async () => {
    if (!activeRelease) return;
    setCheckingOpenStories(true);
    try {
      const response = await axios.get(`${API}/releases/${activeRelease.id}/open-stories`);
      if (response.data.count > 0) {
        setOpenStoriesInfo(response.data);
        setOpenStoriesTargetStatus('');
        setOpenStoriesDialogOpen(true);
      } else {
        setCloseReleaseDialogOpen(true);
      }
    } catch (error) {
      toast.error('Failed to check release status');
    } finally {
      setCheckingOpenStories(false);
    }
  };

  // Called once a target status is chosen for the still-open stories -
  // doesn't close the release itself, just clears the warning and moves on
  // to picking/creating the next release (handleCloseRelease does the rest,
  // carrying openStoriesTargetStatus along).
  const handleContinueAfterOpenStories = () => {
    if (!openStoriesTargetStatus) return;
    setOpenStoriesDialogOpen(false);
    setCloseReleaseDialogOpen(true);
  };

  const handleStartRelease = async (releaseId) => {
    try {
      await axios.post(`${API}/releases/${releaseId}/start`);
      toast.success('Release started');
      fetchActiveRelease(parseInt(filterProject));
      fetchProjectReleases(filterProject);
      fetchReleases();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start release');
    }
  };

  const toggleSelectAll = () => {
    if (selectedStories.length === stories.length) {
      setSelectedStories([]);
    } else {
      setSelectedStories(stories.map(s => s.id));
    }
  };

  const toggleSelectStory = (storyId) => {
    setSelectedStories(prev => 
      prev.includes(storyId) 
        ? prev.filter(id => id !== storyId)
        : [...prev, storyId]
    );
  };

  const handleBulkAction = async () => {
    if (selectedStories.length === 0) {
      toast.error('No stories selected');
      return;
    }

    try {
      if (bulkAction === 'status') {
        await axios.post(`${API}/user-stories/bulk/status`, {
          story_ids: selectedStories,
          status: bulkStatus
        });
        toast.success(`Updated ${selectedStories.length} stories`);
      } else if (bulkAction === 'priority') {
        // Update each story's priority individually
        for (const storyId of selectedStories) {
          await axios.put(`${API}/user-stories/${storyId}`, { priority: bulkPriority });
        }
        toast.success(`Updated priority for ${selectedStories.length} stories`);
      } else if (bulkAction === 'assign') {
        await axios.post(`${API}/user-stories/bulk/assign`, {
          story_ids: selectedStories,
          assigned_users: bulkAssignees
        });
        toast.success(`Assigned users to ${selectedStories.length} stories`);
      } else if (bulkAction === 'release') {
        await axios.post(`${API}/user-stories/bulk/release`, {
          story_ids: selectedStories,
          release_id: bulkReleaseId === 'backlog' ? null : parseInt(bulkReleaseId)
        });
        toast.success(bulkReleaseId === 'backlog' 
          ? `Moved ${selectedStories.length} stories to backlog` 
          : `Updated release for ${selectedStories.length} stories`);
      } else if (bulkAction === 'delete') {
        if (!window.confirm(`Delete ${selectedStories.length} stories?`)) return;
        await axios.post(`${API}/user-stories/bulk/delete`, {
          story_ids: selectedStories
        });
        toast.success(`Deleted ${selectedStories.length} stories`);
      }
      
      setBulkDialogOpen(false);
      setSelectedStories([]);
      setBulkAssignees([]);
      fetchStories();
    } catch (error) {
      toast.error('Bulk operation failed');
    }
  };

  const toggleBulkAssignee = (userId) => {
    setBulkAssignees(prev =>
      prev.includes(userId)
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  const toggleStatusFilter = (statusKey) => {
    setFilterStatuses(prev => {
      if (statusKey === 'all') {
        return prev.includes('all') ? [] : ['all'];
      }
      if (statusKey === 'development') {
        return prev.includes('development') ? [] : ['development'];
      }
      // Remove 'all' and 'development' if selecting specific statuses
      const withoutSpecial = prev.filter(s => s !== 'all' && s !== 'development');
      if (withoutSpecial.includes(statusKey)) {
        return withoutSpecial.filter(s => s !== statusKey);
      }
      return [...withoutSpecial, statusKey];
    });
    setCurrentPage(1);
  };

  const getStatusFilterLabel = () => {
    if (filterStatuses.length === 0) return 'Active Only';
    if (filterStatuses.includes('all')) return 'All Statuses';
    if (filterStatuses.includes('development')) return 'Development';
    if (filterStatuses.length === 1) {
      const status = statuses.find(s => s.key === filterStatuses[0]);
      return status?.name || filterStatuses[0];
    }
    return `${filterStatuses.length} statuses`;
  };

  const openEditDialog = (story, field) => {
    setEditStory(story);
    setEditField(field);
    if (field === 'priority') {
      setEditValue(story.priority || 'medium');
    } else if (field === 'status') {
      setEditValue(story.status || '');
    } else if (field === 'release') {
      setEditValue(story.release_id ? String(story.release_id) : '');
    }
    setEditDialogOpen(true);
  };

  const handleInlineEdit = async () => {
    if (!editStory) return;
    
    try {
      const updateData = {};
      if (editField === 'priority') {
        updateData.priority = editValue;
      } else if (editField === 'status') {
        updateData.status = editValue;
      } else if (editField === 'release') {
        updateData.release_id = editValue ? parseInt(editValue) : null;
      }
      
      await axios.put(`${API}/user-stories/${editStory.id}`, updateData);
      toast.success(`${editField.charAt(0).toUpperCase() + editField.slice(1)} updated`);
      setEditDialogOpen(false);
      setEditStory(null);
      fetchStories();
    } catch (error) {
      toast.error(`Failed to update ${editField}`);
    }
  };

  const getReleasesForStory = (story) => {
    return releases.filter(r => r.project_id === story.project_id);
  };

  const toggleFormAssignee = (userId) => {
    setForm(prev => ({
      ...prev,
      assigned_users: prev.assigned_users.includes(userId)
        ? prev.assigned_users.filter(id => id !== userId)
        : [...prev.assigned_users, userId]
    }));
  };

  const getStatusBadge = (statusKey) => {
    const status = statuses.find(s => s.key === statusKey);
    if (!status) {
      return <Badge variant="secondary">{statusKey}</Badge>;
    }
    return (
      <Badge variant="secondary" className="gap-1.5">
        <div className={`w-2 h-2 rounded-full ${status.color}`} />
        {status.name}
      </Badge>
    );
  };

  const getPriorityBadge = (priority) => {
    const p = PRIORITIES.find(pr => pr.value === priority) || PRIORITIES[3];
    return (
      <Badge variant="outline" className={`${p.color} border`}>
        {p.label}
      </Badge>
    );
  };

  const getUserName = (userId) => {
    const user = users.find(u => u.id === userId);
    return user ? user.name : 'Unknown';
  };

  const handleExport = async (format) => {
    try {
      // Build query params from current filters
      const params = new URLSearchParams();
      params.append('format', format);
      if (filterProject !== 'all') params.append('project_id', filterProject);
      if (filterStatuses.length > 0) params.append('status', filterStatuses[0]); // Use first status
      if (filterPriority !== 'all') params.append('priority', filterPriority);
      if (filterAssignee !== 'all' && filterAssignee !== 'unassigned') {
        params.append('assigned_user_id', filterAssignee);
      }
      
      toast.info(`Generating ${format.toUpperCase()} export...`);
      
      const response = await axios.get(`${API}/export/user-stories?${params.toString()}`, {
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      
      // Get filename from Content-Disposition header or generate one
      const contentDisposition = response.headers['content-disposition'];
      let filename = `user_stories.${format}`;
      if (contentDisposition) {
        const match = contentDisposition.match(/filename=(.+)/);
        if (match) filename = match[1];
      }
      
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success(`Exported ${format.toUpperCase()} successfully`);
    } catch (error) {
      console.error('Export failed:', error);
      toast.error('Failed to export user stories');
    }
  };

  if (loading && stories.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner h-8 w-8" />
      </div>
    );
  }

  return (
    <div
      className="space-y-6"
      data-testid="user-stories-page"
      onDragEnter={handlePageDragEnter}
      onDragOver={handlePageDragOver}
      onDragLeave={handlePageDragLeave}
      onDrop={handlePageDrop}
    >
      {pageDragActive && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-primary/10 backdrop-blur-sm border-4 border-dashed border-primary pointer-events-none" data-testid="page-drop-overlay">
          <div className="bg-background rounded-lg shadow-lg px-8 py-6 flex flex-col items-center gap-3">
            <Upload className="h-10 w-10 text-primary" />
            <p className="text-lg font-medium">Drop screenshot to create a user story</p>
          </div>
        </div>
      )}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-semibold">User Stories</h1>
          <p className="text-muted-foreground mt-1">Manage and track user stories across projects.</p>
        </div>
        <div className="flex items-center gap-2">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" className="gap-2" data-testid="views-btn">
                <Bookmark className="h-4 w-4" />
                Views
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-64">
              {savedViews.length === 0 ? (
                <div className="px-2 py-1.5 text-sm text-muted-foreground">No saved views yet</div>
              ) : (
                savedViews.map((view) => (
                  <DropdownMenuItem
                    key={view.id}
                    className="flex items-center justify-between gap-2"
                    onSelect={() => applyView(view)}
                    data-testid={`saved-view-${view.id}`}
                  >
                    <span className="truncate">{view.name}</span>
                    <button
                      type="button"
                      className="text-muted-foreground hover:text-destructive shrink-0"
                      onClick={(e) => { e.stopPropagation(); e.preventDefault(); deleteView(view.id); }}
                      data-testid={`delete-view-${view.id}`}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </DropdownMenuItem>
                ))
              )}
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onSelect={() => {
                  // Defer opening the Dialog until after the DropdownMenu
                  // finishes closing - opening it synchronously leaves
                  // document.body stuck with pointer-events: none (Radix
                  // dropdown-close/dialog-open race).
                  setTimeout(() => setSaveViewDialogOpen(true), 0);
                }}
                data-testid="save-view-btn"
              >
                <Plus className="h-4 w-4 mr-2" />
                Save current filters as view...
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <Dialog open={saveViewDialogOpen} onOpenChange={setSaveViewDialogOpen}>
            <DialogContent className="sm:max-w-sm">
              <DialogHeader>
                <DialogTitle>Save current filters as view</DialogTitle>
                <DialogDescription>Give this combination of filters and sort order a name so you can reapply it later.</DialogDescription>
              </DialogHeader>
              <div className="space-y-2">
                <Label htmlFor="view-name">Name</Label>
                <Input
                  id="view-name"
                  value={newViewName}
                  onChange={(e) => setNewViewName(e.target.value)}
                  placeholder="e.g. My open critical stories"
                  onKeyDown={(e) => { if (e.key === 'Enter') saveCurrentAsView(); }}
                  data-testid="view-name-input"
                  autoFocus
                />
              </div>
              <div className="flex justify-end gap-2 mt-2">
                <Button variant="outline" onClick={() => setSaveViewDialogOpen(false)}>Cancel</Button>
                <Button onClick={saveCurrentAsView} disabled={!newViewName.trim()} data-testid="save-view-confirm-btn">Save</Button>
              </div>
            </DialogContent>
          </Dialog>
          {hasActiveFilters && (
            <Button 
              variant="outline" 
              onClick={clearFilters}
              className="gap-2"
              data-testid="clear-filters-btn"
            >
              <X className="h-4 w-4" />
              Clear Filters
            </Button>
          )}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" className="gap-2" data-testid="export-btn">
                <Download className="h-4 w-4" />
                Export
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => handleExport('xlsx')} data-testid="export-xlsx">
                <FileSpreadsheet className="h-4 w-4 mr-2" />
                Export to Excel
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleExport('pdf')} data-testid="export-pdf">
                <FileText className="h-4 w-4 mr-2" />
                Export to PDF
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button className="gap-2" data-testid="create-story-btn">
                <Plus className="h-4 w-4" />
                New Story
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Create User Story</DialogTitle>
                <DialogDescription>Add a new user story to a project release</DialogDescription>
              </DialogHeader>
              <form onSubmit={handleCreate} className="space-y-4 mt-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="project">Project *</Label>
                    <Select
                      value={form.project_id ? String(form.project_id) : ''}
                      onValueChange={(value) => setForm({ ...form, project_id: parseInt(value), release_id: '' })}
                      required
                    >
                      <SelectTrigger data-testid="story-project-select">
                        <SelectValue placeholder="Select project" />
                      </SelectTrigger>
                      <SelectContent>
                        {projects.map((project) => (
                          <SelectItem key={project.id} value={String(project.id)}>
                            {project.prefix} - {project.name}
                          </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="release">Release (optional)</Label>
                  <Select
                    value={form.release_id ? String(form.release_id) : 'backlog'}
                    onValueChange={(value) => setForm({ ...form, release_id: value === 'backlog' ? '' : parseInt(value) })}
                    disabled={!form.project_id}
                  >
                    <SelectTrigger data-testid="story-release-select">
                      <SelectValue placeholder={form.project_id ? "Select release" : "Select project first"} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="backlog">
                        <span className="text-muted-foreground">Backlog (no release)</span>
                      </SelectItem>
                      {filteredReleases.map((release) => (
                        <SelectItem key={release.id} value={String(release.id)}>
                          v{release.version} - {release.status}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              {form.project_id && filteredReleases.length === 0 && (
                <p className="text-sm text-muted-foreground">
                  No releases found. Story will be added to backlog. <Link to="/releases" className="text-primary hover:underline">Create releases</Link> to organize your work.
                </p>
              )}
              <div className="space-y-2">
                <Label htmlFor="title">Title *</Label>
                <Input
                  id="title"
                  value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                  placeholder="As a user, I want to..."
                  required
                  data-testid="story-title-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="priority">Priority</Label>
                <Select
                  value={form.priority}
                  onValueChange={(value) => setForm({ ...form, priority: value })}
                >
                  <SelectTrigger data-testid="story-priority-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {PRIORITIES.map((p) => (
                      <SelectItem key={p.value} value={p.value}>
                        {p.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="customer">Customer</Label>
                <Input
                  id="customer"
                  value={form.customer}
                  onChange={(e) => setForm({ ...form, customer: e.target.value })}
                  placeholder="Customer or client name (optional)"
                  data-testid="story-customer-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="description">Description</Label>
                <RichTextEditor
                  id="description"
                  value={form.description}
                  onChange={(value) => setForm({ ...form, description: value })}
                  placeholder="Detailed description..."
                  maxLength={5000}
                  data-testid="story-description-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Assign Users</Label>
                <div className="border rounded-md max-h-32 overflow-y-auto">
                  {users.length === 0 ? (
                    <p className="text-sm text-muted-foreground text-center py-4">No users available</p>
                  ) : (
                    <div className="p-2 space-y-1">
                      {users.map((user) => {
                        const isChecked = form.assigned_users.includes(user.id);
                        return (
                          <div
                            key={user.id}
                            className="flex items-center gap-3 p-2 rounded hover:bg-muted cursor-pointer"
                            onClick={() => toggleFormAssignee(user.id)}
                          >
                            <div className={`h-4 w-4 rounded border flex items-center justify-center ${isChecked ? 'bg-primary border-primary' : 'border-input'}`}>
                              {isChecked && <Check className="h-3 w-3 text-primary-foreground" />}
                            </div>
                            <span className="text-sm">{user.name}</span>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-3 p-3 border rounded-md">
                <div 
                  className={`h-5 w-5 rounded border flex items-center justify-center cursor-pointer ${form.internal ? 'bg-primary border-primary' : 'border-input'}`}
                  onClick={() => setForm({ ...form, internal: !form.internal })}
                  data-testid="story-internal-checkbox"
                >
                  {form.internal && <Check className="h-4 w-4 text-primary-foreground" />}
                </div>
                <div>
                  <Label className="cursor-pointer" onClick={() => setForm({ ...form, internal: !form.internal })}>
                    Internal Story
                  </Label>
                  <p className="text-xs text-muted-foreground">Internal stories are excluded from release reports</p>
                </div>
              </div>
              <div className="space-y-2">
                <Label>Attachments</Label>
                <div
                  onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); setCreateDragActive(true); }}
                  onDragLeave={(e) => { e.preventDefault(); e.stopPropagation(); setCreateDragActive(false); }}
                  onDrop={handleCreateDrop}
                  className={`border-2 border-dashed rounded-md p-4 text-center transition-colors ${createDragActive ? 'border-primary bg-primary/5' : 'border-input'}`}
                  data-testid="story-attachment-dropzone"
                >
                  <Upload className="h-6 w-6 mx-auto text-muted-foreground mb-2" />
                  <p className="text-sm text-muted-foreground">
                    Drag &amp; drop files here, or{' '}
                    <label className="text-primary underline cursor-pointer" htmlFor="create-attachment-input">browse</label>
                  </p>
                  <input
                    id="create-attachment-input"
                    type="file"
                    multiple
                    className="hidden"
                    onChange={(e) => { addPendingFiles(e.target.files); e.target.value = ''; }}
                    data-testid="story-attachment-input"
                  />
                </div>
                {pendingFiles.length > 0 && (
                  <div className="space-y-1" data-testid="pending-attachments-list">
                    {pendingFiles.map((file, index) => (
                      <div key={index} className="flex items-center justify-between text-sm bg-muted/50 rounded px-2 py-1">
                        <span className="flex items-center gap-2 truncate">
                          <Paperclip className="h-3 w-3 flex-shrink-0" />
                          <span className="truncate">{file.name}</span>
                        </span>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="h-6 w-6 p-0"
                          onClick={() => removePendingFile(index)}
                          data-testid={`remove-pending-file-${index}`}
                        >
                          <X className="h-3 w-3" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div className="flex justify-end gap-3 sticky bottom-0 bg-background pt-3 -mb-2 border-t mt-2">
                <Button type="button" variant="outline" onClick={() => { setDialogOpen(false); setPendingFiles([]); }}>
                  Cancel
                </Button>
                <Button type="submit" data-testid="story-submit-btn" disabled={!form.project_id}>Create</Button>
              </div>
            </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4 flex-wrap">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search stories..."
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
              setCurrentPage(1);
            }}
            className="pl-9"
            data-testid="search-stories-input"
          />
        </div>
        <Select value={filterProject} onValueChange={(v) => { setFilterProject(v); setFilterRelease('all'); setCurrentPage(1); }}>
          <SelectTrigger className="w-40" data-testid="filter-project-select">
            <SelectValue placeholder="All Projects" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Projects</SelectItem>
            {projects.map((project) => (
              <SelectItem key={project.id} value={String(project.id)}>
                {project.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        
        {/* Release Filter - only shown when project is selected */}
        {filterProject !== 'all' && (
          <Select value={filterRelease} onValueChange={(v) => { setFilterRelease(v); setCurrentPage(1); }}>
            <SelectTrigger className="w-36" data-testid="filter-release-select">
              <SelectValue placeholder="All Releases" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Releases</SelectItem>
              <SelectItem value="backlog">Backlog (none)</SelectItem>
              {projectReleases.map((release) => (
                <SelectItem key={release.id} value={String(release.id)}>
                  v{release.version}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
        
        {/* Multi-select Status Filter */}
        <DropdownMenu open={statusFilterOpen} onOpenChange={setStatusFilterOpen}>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" className="w-40 justify-between" data-testid="filter-status-select">
              <span className="truncate">{getStatusFilterLabel()}</span>
              <ArrowUpDown className="h-4 w-4 ml-2 opacity-50" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent className="w-48">
            <div 
              className="flex items-center gap-2 px-2 py-1.5 cursor-pointer hover:bg-muted rounded-sm"
              onClick={() => { setFilterStatuses([]); setCurrentPage(1); }}
            >
              <div className={`w-4 h-4 border rounded flex items-center justify-center ${filterStatuses.length === 0 ? 'bg-primary border-primary' : 'border-input'}`}>
                {filterStatuses.length === 0 && <Check className="h-3 w-3 text-primary-foreground" />}
              </div>
              <span className="text-sm">Active Only</span>
            </div>
            <div 
              className="flex items-center gap-2 px-2 py-1.5 cursor-pointer hover:bg-muted rounded-sm"
              onClick={() => toggleStatusFilter('development')}
            >
              <div className={`w-4 h-4 border rounded flex items-center justify-center ${filterStatuses.includes('development') ? 'bg-primary border-primary' : 'border-input'}`}>
                {filterStatuses.includes('development') && <Check className="h-3 w-3 text-primary-foreground" />}
              </div>
              <span className="text-sm">Development</span>
            </div>
            <div 
              className="flex items-center gap-2 px-2 py-1.5 cursor-pointer hover:bg-muted rounded-sm"
              onClick={() => toggleStatusFilter('all')}
            >
              <div className={`w-4 h-4 border rounded flex items-center justify-center ${filterStatuses.includes('all') ? 'bg-primary border-primary' : 'border-input'}`}>
                {filterStatuses.includes('all') && <Check className="h-3 w-3 text-primary-foreground" />}
              </div>
              <span className="text-sm">All Statuses</span>
            </div>
            <DropdownMenuSeparator />
            {statuses.map((status) => (
              <div 
                key={status.key}
                className="flex items-center gap-2 px-2 py-1.5 cursor-pointer hover:bg-muted rounded-sm"
                onClick={() => toggleStatusFilter(status.key)}
              >
                <div className={`w-4 h-4 border rounded flex items-center justify-center ${filterStatuses.includes(status.key) ? 'bg-primary border-primary' : 'border-input'}`}>
                  {filterStatuses.includes(status.key) && <Check className="h-3 w-3 text-primary-foreground" />}
                </div>
                <div 
                  className="w-2 h-2 rounded-full" 
                  style={{ backgroundColor: status.color }}
                />
                <span className="text-sm">{status.name}</span>
              </div>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
        
        <Select value={filterPriority} onValueChange={(v) => { setFilterPriority(v); setCurrentPage(1); }}>
          <SelectTrigger className="w-36" data-testid="filter-priority-select">
            <SelectValue placeholder="All Priorities" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Priorities</SelectItem>
            {PRIORITIES.map((p) => (
              <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={filterAssignee} onValueChange={(v) => { setFilterAssignee(v); setCurrentPage(1); }}>
          <SelectTrigger className="w-40" data-testid="filter-assignee-select">
            <SelectValue placeholder="All Assignees" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Assignees</SelectItem>
            <SelectItem value="me">
              <span className="flex items-center gap-2">
                <User className="h-3 w-3" /> My Stories
              </span>
            </SelectItem>
            <SelectItem value="unassigned">Unassigned</SelectItem>
            {users.map((user) => (
              <SelectItem key={user.id} value={String(user.id)}>{user.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <div className="flex gap-2 items-center">
          <ArrowUpDown className="h-4 w-4 text-muted-foreground" />
          <Select value={sortBy} onValueChange={(v) => { setSortBy(v); setCurrentPage(1); }}>
            <SelectTrigger className="w-36" data-testid="sort-by-select">
              <SelectValue placeholder="Sort by" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="priority">Priority</SelectItem>
              <SelectItem value="created_at">Date Added</SelectItem>
              <SelectItem value="release_version">Release</SelectItem>
              <SelectItem value="title">Title</SelectItem>
              <SelectItem value="issue_number">Issue #</SelectItem>
            </SelectContent>
          </Select>
          <Select value={sortOrder} onValueChange={(v) => { setSortOrder(v); setCurrentPage(1); }}>
            <SelectTrigger className="w-28" data-testid="sort-order-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="desc">High→Low</SelectItem>
              <SelectItem value="asc">Low→High</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Bulk Actions Bar */}
      {selectedStories.length > 0 && (
        <div className="flex items-center gap-4 p-4 bg-primary/5 rounded-lg border border-primary/20">
          <span className="text-sm font-medium">{selectedStories.length} selected</span>
          <div className="flex gap-2 flex-wrap">
            <Button variant="outline" size="sm" onClick={() => { setBulkAction('status'); setBulkDialogOpen(true); }}>
              <CheckSquare className="h-4 w-4 mr-1" /> Status
            </Button>
            <Button variant="outline" size="sm" onClick={() => { setBulkAction('priority'); setBulkDialogOpen(true); }}>
              <AlertTriangle className="h-4 w-4 mr-1" /> Priority
            </Button>
            <Button variant="outline" size="sm" onClick={() => { setBulkAction('release'); setBulkReleaseId('backlog'); setBulkDialogOpen(true); }}>
              <Package className="h-4 w-4 mr-1" /> Release
            </Button>
            <Button variant="outline" size="sm" onClick={() => { setBulkAction('assign'); setBulkDialogOpen(true); }}>
              <Users className="h-4 w-4 mr-1" /> Assign
            </Button>
            <Button variant="outline" size="sm" className="text-destructive" onClick={() => { setBulkAction('delete'); setBulkDialogOpen(true); }}>
              <Trash2 className="h-4 w-4 mr-1" /> Delete
            </Button>
          </div>
          <Button variant="ghost" size="sm" onClick={() => setSelectedStories([])}>Clear</Button>
        </div>
      )}

      {/* Bulk Action Dialog */}
      <Dialog open={bulkDialogOpen} onOpenChange={setBulkDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>
              {bulkAction === 'status' && 'Change Status'}
              {bulkAction === 'priority' && 'Change Priority'}
              {bulkAction === 'release' && 'Change Release'}
              {bulkAction === 'assign' && 'Assign Users'}
              {bulkAction === 'delete' && 'Delete Stories'}
            </DialogTitle>
            <DialogDescription>Affects {selectedStories.length} selected stories</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            {bulkAction === 'status' && (
              <Select value={bulkStatus} onValueChange={setBulkStatus}>
                <SelectTrigger><SelectValue placeholder="Select status" /></SelectTrigger>
                <SelectContent>
                  {statuses.map((status) => (
                    <SelectItem key={status.key} value={status.key}>
                      <div className="flex items-center gap-2">
                        <div className={`w-2 h-2 rounded-full ${status.color}`} />
                        {status.name}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            {bulkAction === 'priority' && (
              <Select value={bulkPriority} onValueChange={setBulkPriority}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {PRIORITIES.map((p) => (
                    <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            {bulkAction === 'release' && (
              <div className="space-y-3">
                <Select value={bulkReleaseId} onValueChange={setBulkReleaseId}>
                  <SelectTrigger><SelectValue placeholder="Select release" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="backlog">
                      <span className="text-muted-foreground">Move to Backlog (no release)</span>
                    </SelectItem>
                    {releases.map((release) => (
                      <SelectItem key={release.id} value={String(release.id)}>
                        {release.project_name} - v{release.version} ({release.status})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {bulkReleaseId === 'backlog' && (
                  <p className="text-sm text-muted-foreground">Stories will be removed from their current release and moved to the backlog.</p>
                )}
              </div>
            )}
            {bulkAction === 'assign' && (
              <div className="border rounded-md max-h-48 overflow-y-auto">
                {users.map((user) => (
                  <div key={user.id} className="flex items-center gap-3 p-2 hover:bg-muted cursor-pointer" onClick={() => toggleBulkAssignee(user.id)}>
                    <Checkbox checked={bulkAssignees.includes(user.id)} onCheckedChange={() => toggleBulkAssignee(user.id)} />
                    <span className="text-sm">{user.name}</span>
                  </div>
                ))}
              </div>
            )}
            {bulkAction === 'delete' && (
              <p className="text-sm text-muted-foreground">This cannot be undone.</p>
            )}
            <div className="flex justify-end gap-3">
              <Button variant="outline" onClick={() => setBulkDialogOpen(false)}>Cancel</Button>
              <Button onClick={handleBulkAction} variant={bulkAction === 'delete' ? 'destructive' : 'default'}>
                {bulkAction === 'delete' ? 'Delete' : 'Apply'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Inline Edit Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>
              {editField === 'priority' && 'Change Priority'}
              {editField === 'status' && 'Change Status'}
              {editField === 'release' && 'Change Release'}
            </DialogTitle>
            <DialogDescription>
              {editStory?.issue_number}: {editStory?.title}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            {editField === 'priority' && (
              <Select value={editValue} onValueChange={setEditValue}>
                <SelectTrigger data-testid="inline-edit-priority-select">
                  <SelectValue placeholder="Select priority" />
                </SelectTrigger>
                <SelectContent>
                  {PRIORITIES.map((p) => (
                    <SelectItem key={p.value} value={p.value}>
                      <div className="flex items-center gap-2">
                        <div className={`w-3 h-3 rounded ${p.color.split(' ')[0]}`} />
                        {p.label}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            {editField === 'status' && (
              <Select value={editValue} onValueChange={setEditValue}>
                <SelectTrigger data-testid="inline-edit-status-select">
                  <SelectValue placeholder="Select status" />
                </SelectTrigger>
                <SelectContent>
                  {statuses.map((status) => (
                    <SelectItem key={status.key} value={status.key}>
                      <div className="flex items-center gap-2">
                        <div 
                          className="w-3 h-3 rounded-full" 
                          style={{ backgroundColor: status.color }}
                        />
                        {status.name}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            {editField === 'release' && editStory && (
              <Select value={editValue} onValueChange={setEditValue}>
                <SelectTrigger data-testid="inline-edit-release-select">
                  <SelectValue placeholder="Select release" />
                </SelectTrigger>
                <SelectContent>
                  {getReleasesForStory(editStory).map((release) => (
                    <SelectItem key={release.id} value={String(release.id)}>
                      v{release.version} - {release.status}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            <div className="flex justify-end gap-3">
              <Button variant="outline" onClick={() => setEditDialogOpen(false)}>Cancel</Button>
              <Button onClick={handleInlineEdit} data-testid="inline-edit-save-btn">Save</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Active Release Banner */}
      {filterProject && filterProject !== 'all' && (
        <div className="p-4 bg-emerald-50 dark:bg-emerald-950/30 rounded-lg border border-emerald-200 dark:border-emerald-800 flex items-center justify-between">
          {activeRelease ? (
            <>
              <div className="flex items-center gap-2">
                <Package className="h-5 w-5 text-emerald-600" />
                <span className="text-sm font-medium text-emerald-800 dark:text-emerald-200">
                  Currently working on <span className="font-bold">v{activeRelease.version}</span>
                </span>
              </div>
              <Button
                variant="outline"
                size="sm"
                className="border-emerald-300 text-emerald-700 hover:bg-emerald-100"
                onClick={handleOpenCloseReleaseClick}
                disabled={checkingOpenStories}
              >
                Close current release
              </Button>
            </>
          ) : (
            <>
              <div className="flex items-center gap-2">
                <Package className="h-5 w-5 text-amber-600" />
                <span className="text-sm text-amber-800 dark:text-amber-200">
                  No active release for this project
                </span>
              </div>
              {plannedReleases.length > 0 ? (
                <Select onValueChange={(value) => handleStartRelease(parseInt(value))}>
                  <SelectTrigger className="w-48 border-amber-300">
                    <SelectValue placeholder="Start a release..." />
                  </SelectTrigger>
                  <SelectContent>
                    {plannedReleases.map((release) => (
                      <SelectItem key={release.id} value={String(release.id)}>
                        v{release.version}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : (
                <Link to="/releases">
                  <Button variant="outline" size="sm" className="border-amber-300 text-amber-700">
                    Create a release
                  </Button>
                </Link>
              )}
            </>
          )}
        </div>
      )}

      {/* Close Release Dialog */}
      <Dialog open={closeReleaseDialogOpen} onOpenChange={setCloseReleaseDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Close Release v{activeRelease?.version}</DialogTitle>
            <DialogDescription>
              This release will be marked as Done. Select the next release to work on, or skip
              and start one later.
              {openStoriesTargetStatus && (
                <> {openStoriesInfo?.count} open {openStoriesInfo?.count === 1 ? 'story' : 'stories'} will
                  also be moved to "{closeableStatuses.find((s) => s.key === openStoriesTargetStatus)?.name}".</>
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="space-y-3">
              <Label>Select next release to work on:</Label>
              
              {plannedReleases.length > 0 && (
                <div className="space-y-2">
                  {plannedReleases.map((release) => (
                    <div
                      key={release.id}
                      className={`flex items-center gap-3 p-3 border rounded-lg cursor-pointer hover:bg-muted ${
                        nextReleaseOption === 'existing' && selectedNextReleaseId === String(release.id)
                          ? 'border-primary bg-primary/5'
                          : ''
                      }`}
                      onClick={() => {
                        setNextReleaseOption('existing');
                        setSelectedNextReleaseId(String(release.id));
                      }}
                    >
                      <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                        nextReleaseOption === 'existing' && selectedNextReleaseId === String(release.id)
                          ? 'border-primary'
                          : 'border-muted-foreground'
                      }`}>
                        {nextReleaseOption === 'existing' && selectedNextReleaseId === String(release.id) && (
                          <div className="w-2 h-2 rounded-full bg-primary" />
                        )}
                      </div>
                      <span className="font-medium">v{release.version}</span>
                      <Badge variant="secondary" className="text-xs">planned</Badge>
                    </div>
                  ))}
                </div>
              )}
              
              <div
                className={`flex items-center gap-3 p-3 border rounded-lg cursor-pointer hover:bg-muted ${
                  nextReleaseOption === 'new' ? 'border-primary bg-primary/5' : ''
                }`}
                onClick={() => setNextReleaseOption('new')}
              >
                <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                  nextReleaseOption === 'new' ? 'border-primary' : 'border-muted-foreground'
                }`}>
                  {nextReleaseOption === 'new' && (
                    <div className="w-2 h-2 rounded-full bg-primary" />
                  )}
                </div>
                <span className="font-medium">Create new release:</span>
                <Input
                  placeholder="e.g., 2.0.0"
                  value={newReleaseVersion}
                  onChange={(e) => {
                    setNewReleaseVersion(e.target.value);
                    setNextReleaseOption('new');
                  }}
                  onClick={(e) => e.stopPropagation()}
                  className="w-32"
                />
              </div>

              <div
                className={`flex items-center gap-3 p-3 border rounded-lg cursor-pointer hover:bg-muted ${
                  nextReleaseOption === 'skip' ? 'border-primary bg-primary/5' : ''
                }`}
                onClick={() => setNextReleaseOption('skip')}
                data-testid="skip-next-release-option"
              >
                <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                  nextReleaseOption === 'skip' ? 'border-primary' : 'border-muted-foreground'
                }`}>
                  {nextReleaseOption === 'skip' && (
                    <div className="w-2 h-2 rounded-full bg-primary" />
                  )}
                </div>
                <span className="font-medium">Don't start a new release yet</span>
              </div>
            </div>

            <div className="flex justify-end gap-3 pt-4 border-t">
              <Button
                variant="outline"
                onClick={() => {
                  setCloseReleaseDialogOpen(false);
                  setOpenStoriesInfo(null);
                  setOpenStoriesTargetStatus('');
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={() => handleCloseRelease(openStoriesTargetStatus)}
                disabled={
                  (nextReleaseOption === 'existing' && !selectedNextReleaseId) ||
                  (nextReleaseOption === 'new' && !newReleaseVersion)
                }
                data-testid="close-release-confirm-btn"
              >
                {nextReleaseOption === 'skip' ? 'Close Release' : 'Start'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Resolve Open Stories Dialog - shown when closing a release finds
          stories that aren't done/rejected yet */}
      <Dialog open={openStoriesDialogOpen} onOpenChange={setOpenStoriesDialogOpen}>
        <DialogContent className="sm:max-w-md" data-testid="open-stories-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-500" />
              Release has open stories
            </DialogTitle>
            <DialogDescription>
              {openStoriesInfo?.message || 'This release still has open user stories.'} Choose a
              status to move them to, then continue to pick the next release - or cancel to keep
              the release open and leave these stories untouched.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-2 min-w-0">
            {openStoriesInfo?.stories?.length > 0 && (
              <div className="border rounded-md divide-y max-h-40 overflow-y-auto overflow-x-hidden text-sm">
                {openStoriesInfo.stories.map((s) => (
                  <div key={s.id} className="px-3 py-2 flex items-center gap-2 min-w-0">
                    <span className="issue-number shrink-0 whitespace-nowrap">{s.issue_number}</span>
                    <span className="truncate min-w-0 flex-1" title={s.title}>{s.title}</span>
                    <span className="shrink-0">{getStatusBadge(s.status)}</span>
                  </div>
                ))}
                {openStoriesInfo.count > openStoriesInfo.stories.length && (
                  <div className="px-3 py-2 text-muted-foreground">
                    and {openStoriesInfo.count - openStoriesInfo.stories.length} more...
                  </div>
                )}
              </div>
            )}
            <div className="space-y-2">
              <Label>Move open stories to:</Label>
              <Select value={openStoriesTargetStatus} onValueChange={setOpenStoriesTargetStatus}>
                <SelectTrigger data-testid="open-stories-status-select">
                  <SelectValue placeholder="Select a status..." />
                </SelectTrigger>
                <SelectContent>
                  {closeableStatuses.map((status) => (
                    <SelectItem key={status.key} value={status.key}>
                      {status.name}{status.is_rejected ? ' (rejected)' : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex justify-end gap-3 pt-2 border-t">
              <Button
                variant="outline"
                onClick={() => {
                  setOpenStoriesDialogOpen(false);
                  setOpenStoriesInfo(null);
                  setOpenStoriesTargetStatus('');
                }}
                data-testid="open-stories-cancel-btn"
              >
                Cancel
              </Button>
              <Button
                onClick={handleContinueAfterOpenStories}
                disabled={!openStoriesTargetStatus}
                data-testid="open-stories-confirm-btn"
              >
                Continue
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Stories List */}
      {stories.length === 0 ? (
        <Card>
          <CardContent className="py-16">
            <div className="empty-state">
              <FileText className="empty-state-icon" />
              <p className="empty-state-title">No user stories found</p>
              <p className="empty-state-description">
                {searchQuery || filterProject !== 'all' || filterStatuses.length > 0
                  ? 'Try adjusting your filters.'
                  : 'Create your first user story to get started.'}
              </p>
            </div>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="p-0">
            <table className="data-table">
              <thead>
                <tr>
                  <th className="w-12">
                    <Checkbox
                      checked={selectedStories.length === stories.length && stories.length > 0}
                      onCheckedChange={toggleSelectAll}
                    />
                  </th>
                  <th className="w-28">Issue</th>
                  <th>Title</th>
                  <th className="hidden md:table-cell w-28">Priority</th>
                  <th className="hidden md:table-cell w-24">Release</th>
                  <th className="w-28">Status</th>
                  <th className="hidden sm:table-cell w-20">Comments</th>
                  <th className="hidden lg:table-cell w-36">Created</th>
                  <th className="w-12"></th>
                </tr>
              </thead>
              <tbody>
                {stories.map((story) => (
                  <tr key={story.id} data-testid={`story-row-${story.issue_number}`}>
                    <td>
                      <Checkbox
                        checked={selectedStories.includes(story.id)}
                        onCheckedChange={() => toggleSelectStory(story.id)}
                      />
                    </td>
                    <td><span className="issue-number">{story.issue_number}</span></td>
                    <td>
                      <Link to={`/user-stories/${story.id}`} className="font-medium hover:text-primary transition-colors">
                        {story.title}
                      </Link>
                      {story.internal && (
                        <Badge 
                          variant="outline" 
                          className="ml-2 h-5 w-5 p-0 inline-flex items-center justify-center text-xs font-medium border-amber-500 text-amber-600 bg-amber-50"
                          title="Internal - excluded from release reports"
                        >
                          i
                        </Badge>
                      )}
                      {story.attachments?.length > 0 && <Paperclip className="inline h-3 w-3 ml-2 text-muted-foreground" />}
                    </td>
                    <td className="hidden md:table-cell">
                      <button
                        onClick={() => openEditDialog(story, 'priority')}
                        className="hover:opacity-80 transition-opacity cursor-pointer"
                        data-testid={`edit-priority-${story.id}`}
                      >
                        {getPriorityBadge(story.priority)}
                      </button>
                    </td>
                    <td className="hidden md:table-cell">
                      <button
                        onClick={() => openEditDialog(story, 'release')}
                        className="hover:opacity-80 transition-opacity cursor-pointer"
                        data-testid={`edit-release-${story.id}`}
                      >
                        {story.release_version ? (
                          <Badge variant="outline" className="font-mono text-xs gap-1">
                            <Package className="h-3 w-3" />v{story.release_version}
                          </Badge>
                        ) : <span className="text-muted-foreground">—</span>}
                      </button>
                    </td>
                    <td>
                      <button
                        onClick={() => openEditDialog(story, 'status')}
                        className="hover:opacity-80 transition-opacity cursor-pointer"
                        data-testid={`edit-status-${story.id}`}
                      >
                        {getStatusBadge(story.status)}
                      </button>
                    </td>
                    <td className="hidden sm:table-cell">
                      <Link 
                        to={`/user-stories/${story.id}`} 
                        className="flex items-center gap-1 text-muted-foreground hover:text-primary transition-colors"
                        data-testid={`story-comments-${story.id}`}
                      >
                        <MessageSquare className="h-4 w-4" />
                        <span className="text-sm">{story.comment_count || 0}</span>
                      </Link>
                    </td>
                    <td className="hidden lg:table-cell text-muted-foreground text-sm">
                      <Clock className="inline h-3 w-3 mr-1" />
                      {format(new Date(story.created_at), 'MMM d, yyyy')}
                    </td>
                    <td>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" className="h-8 w-8">
                            <MoreVertical className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem asChild>
                            <Link to={`/user-stories/${story.id}`}>View Details</Link>
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem className="text-destructive" onClick={() => handleDelete(story.id)}>
                            Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              totalItems={totalItems}
              pageSize={pageSize}
              onPageChange={setCurrentPage}
            />
          </CardContent>
        </Card>
      )}
    </div>
  );
};
